// use-hl-order-book — 1s-Poll von /info `l2Book` für L2-Snapshot pro Coin.
// (WS l2Book wäre niedrigere Latenz, aber Poll reicht für die UI und ist
// deutlich simpler — kein Subscribe-Lifecycle pro Coin-Wechsel.)
//
// Returns Top-N Levels pro Seite + computed Imbalance + computed Spread.

import { useEffect, useMemo, useState } from 'react';
import { fetchL2Book, type L2Level, type L2Snapshot } from '@/lib/hl-info-api';

const POLL_INTERVAL_MS = 1_000;
const DEFAULT_DEPTH = 10;

export interface UseHlOrderBookOptions {
  enabled?: boolean;
  pollIntervalMs?: number;
  /** Anzahl Levels pro Seite (default 10). */
  depth?: number;
}

export interface UseHlOrderBookReturn {
  bids: L2Level[];
  asks: L2Level[];
  midPrice: number | null;
  /** Spread in USD. */
  spreadUsd: number | null;
  /** Imbalance: bid-Volumen / (bid+ask)-Volumen über depth-Levels. 0.5 = neutral. */
  imbalance: number | null;
  loading: boolean;
  /** Server-Timestamp des letzten Snapshots. */
  time: number | null;
}

export function useHlOrderBook(
  coin: string,
  options: UseHlOrderBookOptions = {},
): UseHlOrderBookReturn {
  const { enabled = true, pollIntervalMs = POLL_INTERVAL_MS, depth = DEFAULT_DEPTH } = options;
  const [snapshot, setSnapshot] = useState<L2Snapshot | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!enabled || !coin) {
      setSnapshot(null);
      return undefined;
    }
    let cancelled = false;

    const poll = async (): Promise<void> => {
      if (cancelled) return;
      setLoading(true);
      try {
        const next = await fetchL2Book(coin);
        if (!cancelled && next) setSnapshot(next);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    void poll();
    const id = setInterval(() => void poll(), pollIntervalMs);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [enabled, coin, pollIntervalMs]);

  return useMemo(() => {
    const bids = (snapshot?.bids ?? []).slice(0, depth);
    const asks = (snapshot?.asks ?? []).slice(0, depth);

    const bestBid = bids[0]?.price ?? null;
    const bestAsk = asks[0]?.price ?? null;
    const midPrice =
      bestBid !== null && bestAsk !== null ? (bestBid + bestAsk) / 2 : null;
    const spreadUsd =
      bestBid !== null && bestAsk !== null ? bestAsk - bestBid : null;

    const bidVol = bids.reduce((s, l) => s + l.size, 0);
    const askVol = asks.reduce((s, l) => s + l.size, 0);
    const total = bidVol + askVol;
    const imbalance = total > 0 ? bidVol / total : null;

    return {
      bids,
      asks,
      midPrice,
      spreadUsd,
      imbalance,
      loading,
      time: snapshot?.time ?? null,
    };
  }, [snapshot, depth, loading]);
}
